import React, { useState, useContext } from 'react';
import Sidebar from './Sidebar';
import { AuthContext } from '../context/AuthContext';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hasSeenNotification, setHasSeenNotification] = useState(false);
  const { user } = useContext(AuthContext);

  // Helper function to get membership status
  const getMembershipStatus = () => {
    if (!user?.expiryDate) return { status: 'none', color: 'green' };
    
    const expiryDate = new Date(user.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'red' };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', color: 'amber' };
    } else {
      return { status: 'active', color: 'green' };
    }
  };

  // Read app name from environment variable, default to 'Court Booking System' if not set
  const appName = process.env.REACT_APP_APP_NAME || 'Court Booking System';

  const membershipStatus = getMembershipStatus();
  const showNotification = (membershipStatus.color === 'amber' || membershipStatus.color === 'red') && !hasSeenNotification;

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true);
    setHasSeenNotification(true);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header with hamburger menu and welcome message */}
      <header className="bg-white shadow-sm border-b border-slate-500">
        <div className="h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo192.png" alt="Logo" className="h-16 w-16 mr-2" />
            <div className="flex flex-col">
              <h1 className="text-xl md:text-2xl font-bold text-slate-800">Welcome, {user?.firstName}!</h1>
              <p className="text-xs text-slate-800">{appName}</p>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={handleOpenSidebar}
              className="p-4 rounded-md text-slate-600 hover:text-slate-800 focus:outline-none relative"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>

              {/* Notification indicator */}
              {showNotification && (
                <div
                  className={`absolute top-2 left-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white ${
                    membershipStatus.color === "amber" ? "bg-orange-400" : "bg-red-600"
                  }`}
                ><span className="text-xs font-semibold text-white">1</span></div>
              )}
              
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="p-0">
        {children}
      </main>

      {/* Footer */}
      <footer className="text-sm text-slate-500 text-center py-8 relative">
        <div className="space-y-2">
          <div>© 2025 Trelody (Pty) Ltd — <a href="https://courtbookings.co.za" className="underline hover:text-slate-700" target="_blank" rel="noopener noreferrer">courtbookings.co.za</a></div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs text-slate-400">Powered by</span>
            <span className="font-gruppo text-lg text-slate-400 lowercase tracking-wide">trelody</span>
          </div>
          <div className="flex justify-center gap-4 text-xs">
            <a
              className="underline hover:text-slate-700"
              href="https://courtbookings.co.za/privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
            <a
              className="underline hover:text-slate-700"
              href="https://courtbookings.co.za/terms/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
          </div>
        </div>
      </footer>

      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
    </div>
  );
};

export default Layout; 